<template>
  <div>
    <branch-list-add-new
      :is-add-new-branch-sidebar-active.sync="isAddNewBranchSidebarActive"
      :role-options="roleOptions"
      @refetch-data="refetchData"
    />

    <branch-list-edit
      :is-edit-branch-sidebar-active.sync="isEditBranchSidebarActive"
      :branch-id="branchId"
      :branch-name="branchName"
      :branch-code="branchCode"
      :branch-city="branchCity"
      :branch-address="branchAddress"
      :branch-phone="branchPhone"
      :branch-fax="branchFax"
      :branch-region-id="branchRegionId"
    />

    <app-page-shell
      title="Branches"
      subtitle="Manage branch identity, region mapping, and contact information."
    >
      <div
        v-if="userRole === 'super-admin'"
        class="m-2"
      >
        <b-row>
          <b-col
            cols="12"
            sm="6"
            lg="4"
            class="mb-1 mb-lg-0"
          >
            <statistic-card-horizontal
              icon="GridIcon"
              color="primary"
              :statistic="statsLoading ? '...' : branchStats.total"
              statistic-title="Total Branches"
            />
          </b-col>
          <b-col
            cols="12"
            sm="6"
            lg="4"
            class="mb-1 mb-lg-0"
          >
            <statistic-card-horizontal
              icon="CheckCircleIcon"
              color="success"
              :statistic="statsLoading ? '...' : branchStats.active"
              statistic-title="Active Branches"
            />
          </b-col>
          <b-col
            cols="12"
            sm="6"
            lg="4"
          >
            <statistic-card-horizontal
              icon="XCircleIcon"
              color="danger"
              :statistic="statsLoading ? '...' : branchStats.inactive"
              statistic-title="Inactive Branches"
            />
          </b-col>
        </b-row>
      </div>

      <div class="m-2">
        <b-row>
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label>Show</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
            />
            <label>entries</label>
          </b-col>

          <b-col
            cols="12"
            md="6"
          >
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                v-model="searchQuery"
                class="d-inline-block mr-1"
                placeholder="Search..."
              />
              <b-button
                variant="primary"
                @click="isAddNewBranchSidebarActive = true"
              >
                <span class="text-nowrap">Add Branch</span>
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        ref="refBranchListTable"
        class="position-relative"
        :items="fetchBranchs"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        empty-text="No matching records found"
        :sort-desc.sync="isSortDirDesc"
        :busy="loading"
      >
        <template #table-busy>
          <b-skeleton-table
            :rows="perPage || 5"
            :columns="tableColumns ? tableColumns.length : 6"
            :table-props="{ bordered: true, striped: true }"
          />
        </template>

        <template #cell(branch_name)="data">
          <div class="text-nowrap">
            <span class="align-text-top text-capitalize">{{ data.item.branch_name }}</span>
          </div>
        </template>

        <template #cell(branch_code)="data">
          <div class="text-nowrap">
            <span class="align-text-top text-capitalize">{{ data.item.branch_code }}</span>
          </div>
        </template>

        <template #cell(branch_city)="data">
          <div class="text-nowrap">
            <span class="align-text-top text-capitalize">{{ data.item.branch_city }}</span>
          </div>
        </template>

        <template #cell(branch_region)="data">
          <div class="text-nowrap">
            <span class="align-text-top text-capitalize">{{ data.item.region ? data.item.region.region_name : '-' }}</span>
          </div>
        </template>

        <template #cell(branch_status)="data">
          <div
            v-if="userRole === 'super-admin'"
            class="text-nowrap"
          >
            <b-form-checkbox
              :checked="data.item.branch_is_active"
              switch
              :disabled="togglingId === data.item.branch_id"
              @change="toggleStatus(data.item)"
            >
              <span class="align-middle">{{ data.item.branch_is_active ? 'Active' : 'Inactive' }}</span>
            </b-form-checkbox>
          </div>
          <b-badge
            v-else
            :variant="data.item.branch_is_active ? 'light-success' : 'light-danger'"
          >
            {{ data.item.branch_is_active ? 'Active' : 'Inactive' }}
          </b-badge>
        </template>

        <template #cell(actions)="data">
          <b-dropdown
            variant="link"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>

            <b-dropdown-item @click="editBranch(data.item)">
              <span class="align-middle ml-50">Edit</span>
            </b-dropdown-item>

            <b-dropdown-item @click="deleteBranch(data.item.branch_id)">
              <span class="align-middle ml-50">Delete</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>

      </b-table>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">Showing {{ dataMeta.from }} to {{ dataMeta.to }} of {{ dataMeta.of }} entries</span>
          </b-col>

          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="totalBranchs"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </app-page-shell>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormInput, BButton, BTable, BMedia, BAvatar, BLink,
  BBadge, BDropdown, BDropdownItem, BPagination, BSkeletonTable, BFormCheckbox,
} from 'bootstrap-vue'
import axios from 'axios'
import vSelect from 'vue-select'
import store from '@/store'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { ref, onUnmounted } from '@vue/composition-api'
import { avatarText } from '@core/utils/filter'

import apiEndpoints from '@/constants/apiEndpoints'
import AppPageShell from '@/views/apps/components/AppPageShell.vue'
import StatisticCardHorizontal from '@core/components/statistics-cards/StatisticCardHorizontal.vue'
import useBranchsList from './BranchList'
import branchModule from '../branchModule'
import BranchListAddNew from './BranchAddNew.vue'
import BranchListEdit from './BranchEdit.vue'

export default {
  components: {
    BranchListEdit,
    BranchListAddNew,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BSkeletonTable,
    BFormCheckbox,
    vSelect,
    AppPageShell,
    StatisticCardHorizontal,
  },
  data() {
    return {
      userRole: '',
      togglingId: null,
    }
  },
  setup() {
    const APP_STORE_MODULE_NAME = 'app-unit'

    if (!store.hasModule(APP_STORE_MODULE_NAME)) {
      store.registerModule(APP_STORE_MODULE_NAME, branchModule)
    }

    onUnmounted(() => {
      if (store.hasModule(APP_STORE_MODULE_NAME)) {
        store.unregisterModule(APP_STORE_MODULE_NAME)
      }
    })

    const isAddNewBranchSidebarActive = ref(false)
    const isEditBranchSidebarActive = ref(false)

    // Extracted out of global scope and cleanly defined in setup
    const branchId = ref('')
    const branchName = ref('')
    const branchCode = ref('')
    const branchCity = ref('')
    const branchAddress = ref('')
    const branchPhone = ref('')
    const branchFax = ref('')
    const branchRegionId = ref('')

    const statusOptions = [
      { label: 'Pending', value: 'pending' },
      { label: 'Active', value: 'active' },
      { label: 'Inactive', value: 'inactive' },
    ]

    const {
      fetchBranchs,
      fetchRoles,
      fetchBranchStats,
      toggleBranchStatus,
      tableColumns,
      perPage,
      currentPage,
      totalBranchs,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refBranchListTable,
      refetchData,
      branchStats,
      statsLoading,
      roleFilter,
      statusFilter,
      loading,
    } = useBranchsList()

    const roleOptions = [
      { label: 'Admin', value: 'admin' },
      { label: 'Author', value: 'author' },
      { label: 'Editor', value: 'editor' },
      { label: 'Maintainer', value: 'maintainer' },
      { label: 'Subscriber', value: 'subscriber' },
    ]

    return {
      branchId,
      branchName,
      branchCode,
      branchCity,
      branchAddress,
      branchPhone,
      branchFax,
      branchRegionId,
      isEditBranchSidebarActive,
      isAddNewBranchSidebarActive,
      fetchBranchs,
      fetchRoles,
      fetchBranchStats,
      toggleBranchStatus,
      tableColumns,
      perPage,
      currentPage,
      totalBranchs,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refBranchListTable,
      refetchData,
      branchStats,
      statsLoading,
      avatarText,
      roleOptions,
      statusOptions,
      roleFilter,
      statusFilter,
      loading,
    }
  },
  mounted() {
    this.userRole = this.$cookies.get('UserRole')
    if (this.userRole === 'super-admin') {
      this.fetchBranchStats()
    }
    this.$root.$on('refreshTable', () => {
      if (this.$refs.refBranchListTable) {
        if (this.$refs.refBranchListTable) this.$refs.refBranchListTable.refresh()
      }
    })
  },
  methods: {
    toggleStatus(branch) {
      this.togglingId = branch.branch_id
      this.toggleBranchStatus(branch.branch_id)
        .then(response => {
          if (response.success === true) {
            this.refetchData()
            this.fetchBranchStats()
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: 'Success',
                icon: 'CheckIcon',
                variant: 'success',
                text: response.message || 'Branch status updated',
              },
            })
          } else {
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: 'Notification',
                icon: 'BellIcon',
                variant: 'danger',
                text: response.message || 'Something went wrong',
              },
            })
          }
        })
        .catch(err => {
          console.error(err)
        })
        .finally(() => {
          this.togglingId = null
        })
    },
    deleteBranch(id) {
      const userToken = this.$cookies.get('userToken')
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userToken}`,
      }
      const url = `${process.env.VUE_APP_API_URL}${apiEndpoints.API_DELETE_BRANCH}/${id}`

      axios
        .get(url, { headers })
        .then(response => {
          if (response.data.success === true) {
            if (this.$refs.refBranchListTable) this.$refs.refBranchListTable.refresh()
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: 'Success',
                icon: 'CheckIcon',
                variant: 'success',
                text: 'Your data has been deleted!',
              },
            })
          } else {
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: 'Notification',
                icon: 'BellIcon',
                variant: 'danger',
                text: response.data.message || 'Something went wrong',
              },
            })
          }
        })
        .catch(err => {
          console.error(err)
        })
    },
    editBranch(data) {
      this.branchId = data.branch_id.toString()
      this.branchName = data.branch_name
      this.branchCode = data.branch_code
      this.branchCity = data.branch_city
      this.branchAddress = data.branch_address
      this.branchPhone = data.branch_phone
      this.branchFax = data.branch_fax
      this.branchRegionId = data.branch_region_id

      this.isEditBranchSidebarActive = true
    },
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
